/**
 * The links of one paragraph, read once for every reader of them: the striker that
 * keeps edits within links and strikes the ones it must (`LinkFieldStriker`), the
 * engine that writes in front of a link, and the reader that reports each link's
 * words and address to the core (`OoxmlRange.documentParagraphs`), so the agent and
 * the edit model are shown the links the engine writes around.
 *
 * CASES, by how the paragraph spells a link
 *   - a complex HYPERLINK field -> its result runs, opened by its `fldChar begin` run, its address
 *                                  read from its instruction
 *   - a `w:fldSimple` HYPERLINK -> its runs, opened by the `w:fldSimple`, its address read from
 *                                  its `w:instr`
 *   - a `w:hyperlink` element   -> its runs, opened by the element, its address read from the
 *                                  package relationship its `r:id` names
 *
 * WHAT IS NO LINK HERE
 *   A field Word redraws (REF, TOC, PAGEREF, …): its result is Word's, not the author's
 *   (`fieldResultIsRedrawnByWord`). A link whose every run is struck holds no live text.
 */
import { type HyperlinkTargetsByRelationshipId, hyperlinkElementAddressOf } from "./hyperlink-targets";
import { fieldResultIsRedrawnByWord, fieldSimpleInstructionOf, hyperlinkAddressOfFieldInstruction, WORDPROCESSINGML_NS } from "./ooxml-names";
import type { ParagraphModel } from "./paragraph-model";
import { type AcceptedTextRange, complexFieldWalkOf } from "./word-owned-text-unlinker";
import type { ParagraphLink } from "../wire/document-paragraph";

/**
 * Where a link's address is read from, by how the paragraph spells the link.
 *
 * CASES
 *   - "fieldInstruction" -> a complex HYPERLINK field or a `w:fldSimple` one: the instruction names the address
 *   - "hyperlinkElement" -> a `w:hyperlink` element: its `r:id` names a relationship of the package
 */
type LinkAddressSource = { spelling: "fieldInstruction"; fieldInstruction: string } | { spelling: "hyperlinkElement"; hyperlinkElement: Element };

/** One link of the paragraph that holds live text: the bounds of that text in the accepted text, the element that opens the link, and where its address is read from. */
export interface LinkWithLiveText extends AcceptedTextRange {
    linkOpeningElement: Element;
    addressSource: LinkAddressSource;
}

/** One link's runs, the element that opens it and where its address is read from, before its live text is read. */
interface LinkRuns {
    linkRuns: Element[];
    linkOpeningElement: Element;
    addressSource: LinkAddressSource;
}

export class ParagraphLinks {
    /**
     * Every link of the paragraph that holds live text, in each spelling, with the
     * bounds of that text and the element that opens the link.
     *
     * NULL
     *   A link whose every run is struck holds no live text and is left out: no edit can
     *   cross its bounds or be written at its start.
     *
     * EXAMPLE
     *   "see the Fee Schedule now", a HYPERLINK field over "the Fee Schedule" -> [4..20, its begin run]
     */
    static withLiveTextOf(paragraphModel: ParagraphModel): LinkWithLiveText[] {
        const paragraph = paragraphModel.paragraph;
        const hyperlinkElements = Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "hyperlink"));
        const fieldSimples = Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "fldSimple"));
        const holdsComplexFields = paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "fldChar").length > 0;
        /** No link markup in any spelling: no link to read. */
        if (hyperlinkElements.length === 0 && fieldSimples.length === 0 && !holdsComplexFields) return [];
        const runsOfEachLink: LinkRuns[] = [
            ...complexFieldWalkOf(paragraph)
                .fields.filter((field) => field.complete && !fieldResultIsRedrawnByWord(field.fieldInstruction))
                .map((field) => ({ linkRuns: field.resultRuns, linkOpeningElement: field.runsBeginToEnd[0], addressSource: { spelling: "fieldInstruction" as const, fieldInstruction: field.fieldInstruction } })),
            ...fieldSimples
                .filter((fieldSimple) => !fieldResultIsRedrawnByWord(fieldSimpleInstructionOf(fieldSimple)))
                .map((fieldSimple) => ({
                    linkRuns: Array.from(fieldSimple.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r")),
                    linkOpeningElement: fieldSimple,
                    addressSource: { spelling: "fieldInstruction" as const, fieldInstruction: fieldSimpleInstructionOf(fieldSimple) },
                })),
            ...hyperlinkElements.map((hyperlinkElement) => ({
                linkRuns: Array.from(hyperlinkElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r")),
                linkOpeningElement: hyperlinkElement,
                addressSource: { spelling: "hyperlinkElement" as const, hyperlinkElement },
            })),
        ];
        const acceptedPieces = paragraphModel.acceptedPieces();
        const linksWithLiveText: LinkWithLiveText[] = [];
        for (const { linkRuns, linkOpeningElement, addressSource } of runsOfEachLink) {
            const linkPieces = acceptedPieces.filter((piece) => linkRuns.includes(piece.run));
            /** A link with no live text has no bounds an edit could cross. */
            if (linkPieces.length === 0) continue;
            linksWithLiveText.push({
                startOffset: Math.min(...linkPieces.map((piece) => piece.startOffset)),
                endOffset: Math.max(...linkPieces.map((piece) => piece.endOffset)),
                linkOpeningElement,
                addressSource,
            });
        }
        return linksWithLiveText;
    }

    /**
     * The address one link points to (`linkAddressOf`'s spelling), read by its spelling.
     *
     * NULL
     *   A field instruction that names no target or place, or an element whose
     *   relationship the package does not hold (or a paragraph read without its package).
     */
    static addressOf(link: LinkWithLiveText, hyperlinkTargets: HyperlinkTargetsByRelationshipId): string | null {
        const { addressSource } = link;
        /**
         * CASES
         *   - a HYPERLINK field          -> read from its instruction: ' HYPERLINK "https://x.example/fee" '
         *   - a `w:hyperlink` element    -> read from the package's relationship its `r:id` names
         */
        if (addressSource.spelling === "fieldInstruction") return hyperlinkAddressOfFieldInstruction(addressSource.fieldInstruction);
        return hyperlinkElementAddressOf(addressSource.hyperlinkElement, hyperlinkTargets);
    }

    /**
     * Whether one edit changes the words of a link's display text: the one reading the
     * web's apply (`EditOperations`, which asks for an instruction on such an edit) and
     * the engine's striker (`LinkFieldStriker`, which writes the link by it) both make.
     *
     * CASES
     *   - an edit that covers characters -> it reaches the link when it overlaps the link's text
     *   - an insertion                   -> only strictly inside the link's text: one at either
     *                                       edge is written beside the link, outside it
     *
     * EXAMPLE, link "the Fee Schedule" at 4..20
     *   edit 8..11  -> reaches: it replaces "Fee"
     *   insert 12   -> reaches: "Fee Payment Schedule"
     *   insert 4    -> does not: written in front of the link
     *   insert 20   -> does not: written after it
     *   edit 20..24 -> does not: it starts where the link ends
     */
    static editReachesLinkText(edit: AcceptedTextRange, linkText: AcceptedTextRange): boolean {
        /** WHEN the edit inserts CONSEQUENCE it reaches the link only strictly inside its text. */
        if (edit.startOffset === edit.endOffset) return edit.startOffset > linkText.startOffset && edit.startOffset < linkText.endOffset;
        return edit.startOffset < linkText.endOffset && edit.endOffset > linkText.startOffset;
    }

    /**
     * Every link of the paragraph as the reader reports it: the bounds of its display
     * text in the accepted text, that text, and its address.
     *
     * EXAMPLE
     *   "see the Fee Schedule now", a link to https://x.example/fee over "the Fee Schedule"
     *   -> [{ startOffset: 4, endOffset: 20, displayText: "the Fee Schedule", address: "https://x.example/fee" }]
     */
    static readingOf(paragraphModel: ParagraphModel, hyperlinkTargets: HyperlinkTargetsByRelationshipId): ParagraphLink[] {
        return ParagraphLinks.withLiveTextOf(paragraphModel).map((link) => ({
            startOffset: link.startOffset,
            endOffset: link.endOffset,
            /** `startOffset` is the link's first accepted-text character, `endOffset` the first one after it: 4..20 of "see the Fee Schedule now" -> "the Fee Schedule". */
            displayText: paragraphModel.text.slice(link.startOffset, link.endOffset),
            address: ParagraphLinks.addressOf(link, hyperlinkTargets),
        }));
    }
}
